import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search as SearchIcon } from 'lucide-react';
import {
  Avatar, Badge, Checkbox, Modal, ModalBody, ModalHeader, Popover,
} from '@vd/ui';
import { DateRangePicker } from './DateRangePicker';
import type { Project, TicketSummary, User } from '@vd/types';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { When } from '../../lib/When';
import styles from './AdvancedSearchModal.module.css';
import { wallTimeEndIn, wallTimeIn } from '../../lib/time';

export interface AdvancedSearchModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected project. The board page passes its current project; the
   * inbox passes undefined since it's cross-project. The user can change
   * it inside the modal regardless. */
  defaultProjectId?: string;
}

type MatchScope = 'title' | 'description' | 'userComments' | 'aiComments' | 'url';
type Annotated = 'yes' | 'no' | 'both';
type Status = 'resolved' | 'unresolved' | 'all';

const ALL_MATCH: ReadonlyArray<{ key: MatchScope; label: string }> = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'userComments', label: 'User comments' },
  { key: 'aiComments', label: 'AI comments' },
  { key: 'url', label: 'Source URL' },
];

export function AdvancedSearchModal({ open, onClose, defaultProjectId }: AdvancedSearchModalProps) {
  const navigate = useNavigate();
  const { currentCompanyId } = useAuth();

  const [q, setQ] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [matchIn, setMatchIn] = useState<Set<MatchScope>>(new Set(ALL_MATCH.map((m) => m.key)));
  const [includeArchived, setIncludeArchived] = useState(false);
  const [annotated, setAnnotated] = useState<Annotated>('both');
  const [status, setStatus] = useState<Status>('all');
  const [assigneeIds, setAssigneeIds] = useState<Set<string>>(new Set());
  const [authorIds, setAuthorIds] = useState<Set<string>>(new Set());
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');

  // Reset every filter on each open. Earlier draft preserved state to
  // support "close → reopen to copy a result URL", but in practice
  // that meant a user navigating to a different board would still see
  // last week's date filter active. Result-URL-copy is better served
  // by adding a "copy link to this ticket" action on each row.
  useEffect(() => {
    if (!open) return;
    setQ('');
    setProjectId(defaultProjectId ?? '');
    setMatchIn(new Set(ALL_MATCH.map((m) => m.key)));
    setIncludeArchived(false);
    setAnnotated('both');
    setStatus('all');
    setAssigneeIds(new Set());
    setAuthorIds(new Set());
    setCreatedFrom('');
    setCreatedTo('');
    setUpdatedFrom('');
    setUpdatedTo('');
  }, [open, defaultProjectId]);

  const projectsQ = useQuery({
    // currentCompanyId in the key so switching companies doesn't show
    // last company's project list until manual invalidation.
    queryKey: ['projects-for-search', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([]),
    enabled: open && !!currentCompanyId,
  });

  const membersQ = useQuery({
    queryKey: ['members-for-search', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: open && !!currentCompanyId,
  });
  const members = useMemo<User[]>(() => (membersQ.data ?? []).map((m) => m.user), [membersQ.data]);

  // Debounce q so each keystroke doesn't fire a request. 250 ms matches
  // the simple-search box on the board / inbox so the two feel identical.
  const debouncedQ = useDebouncedValue(q, 250);
  const ready = debouncedQ.trim().length >= 2;

  // The moments the date filters name, in the person's zone (TOADO-904): the
  // same values key the cache and go in the request, so a change of zone is a
  // new search (Codex, round 3).
  const bounds = {
    createdFrom: instantOf(createdFrom), createdTo: instantOf(createdTo, true),
    updatedFrom: instantOf(updatedFrom), updatedTo: instantOf(updatedTo, true),
  };
  const resultsQ = useQuery({
    queryKey: [
      'advanced-search',
      currentCompanyId, projectId, debouncedQ, includeArchived, annotated, status,
      Array.from(matchIn).sort().join(','),
      Array.from(assigneeIds).sort().join(','),
      Array.from(authorIds).sort().join(','),
      bounds.createdFrom, bounds.createdTo, bounds.updatedFrom, bounds.updatedTo,
    ],
    queryFn: () => api.searchTickets(debouncedQ, {
      companyId: currentCompanyId ?? undefined,
      projectId: projectId || undefined,
      matchIn: Array.from(matchIn),
      includeArchived,
      annotated,
      status,
      assigneeIds: Array.from(assigneeIds),
      authorIds: Array.from(authorIds),
      // As moments in the person's zone (TOADO-904): the server reads an
      // offset-free time in its own.
      ...bounds,
      limit: 100,
    }),
    enabled: open && ready,
    staleTime: 30_000,
    // Cap the per-keystroke cache lifetime — without this, a typist
    // generates dozens of stale entries that linger for the default
    // 5-minute GC window.
    gcTime: 60_000,
  });

  const projects: Project[] = projectsQ.data ?? [];
  const projectsById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const membersById = useMemo(() => new Map(members.map((u) => [u.id, u])), [members]);

  const toggleMatch = (k: MatchScope) => {
    setMatchIn((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      // Don't allow deselecting all — would lie about server behavior
      // (server treats empty as "all").
      if (n.size === 0) return prev;
      return n;
    });
  };

  const onResultClick = (t: TicketSummary) => {
    navigate(`/tickets/${t.id}`);
    onClose();
  };

  const items = resultsQ.data?.items ?? [];

  return (
    <Modal open={open} onClose={onClose} width={1100}>
      <ModalHeader>Advanced search</ModalHeader>
      <ModalBody>
        <div className={styles.queryRow}>
          <SearchIcon size={14} className={styles.queryIcon} />
          <input
            autoFocus
            className={styles.queryInput}
            placeholder="What are you looking for? (≥ 2 characters)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button type="button" className={styles.queryClear} onClick={() => setQ('')} aria-label="Clear">×</button>
          )}
        </div>

        <div className={styles.twoPanel}>
          <div className={styles.leftPanel}>
            <Section title="Match in">
              <div className={styles.checkRow}>
                {ALL_MATCH.map((m) => (
                  <label key={m.key} className={styles.checkLabel}>
                    <Checkbox checked={matchIn.has(m.key)} onChange={() => toggleMatch(m.key)} />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            </Section>

            <Section title="Project">
              <select
                className={styles.select}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">All projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Section>

            <Section title="Status">
              <div className={styles.radioRow}>
                {(['all', 'unresolved', 'resolved'] as const).map((s) => (
                  <label key={s} className={styles.radio}>
                    <input type="radio" name="adv-status" checked={status === s} onChange={() => setStatus(s)} />
                    <span style={{ textTransform: 'capitalize' }}>{s}</span>
                  </label>
                ))}
              </div>
              <label className={styles.checkLabel}>
                <Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
                <span>Include archived</span>
              </label>
            </Section>

            <Section title="Annotated">
              <div className={styles.radioRow}>
                {(['both', 'yes', 'no'] as const).map((a) => (
                  <label key={a} className={styles.radio}>
                    <input type="radio" name="adv-annotated" checked={annotated === a} onChange={() => setAnnotated(a)} />
                    <span>{a === 'yes' ? 'Annotated' : a === 'no' ? 'Not annotated' : 'Both'}</span>
                  </label>
                ))}
              </div>
            </Section>

            <Section title="Author">
              <MemberMultiSelect
                members={members}
                value={authorIds}
                onChange={setAuthorIds}
                placeholder="Any author"
                ariaLabel="Filter by author"
              />
            </Section>

            <Section title="Assignees">
              <MemberMultiSelect
                members={members}
                value={assigneeIds}
                onChange={setAssigneeIds}
                placeholder="Anyone"
                ariaLabel="Filter by assignee"
              />
            </Section>

            <Section title="Created">
              <DateRangePicker
                from={createdFrom}
                to={createdTo}
                onChange={({ from: f, to: t }) => { setCreatedFrom(f); setCreatedTo(t); }}
                placeholder="Any creation date"
                ariaLabel="Filter by creation date range"
              />
            </Section>

            <Section title="Last updated">
              <DateRangePicker
                from={updatedFrom}
                to={updatedTo}
                onChange={({ from: f, to: t }) => { setUpdatedFrom(f); setUpdatedTo(t); }}
                placeholder="Any update date"
                ariaLabel="Filter by last-updated date range"
              />
            </Section>
          </div>

          <div className={styles.rightPanel}>
            <div className={styles.resultsHeader}>
              <span className={styles.resultsTitle}>
                Results
                {ready && resultsQ.isPending && ' (searching…)'}
                {ready && !resultsQ.isPending && ` (${items.length}${items.length === 100 ? '+' : ''})`}
                {!ready && ' (type 2+ characters)'}
              </span>
            </div>
            <div className={styles.resultsList}>
              {!ready ? (
                <div className={styles.emptyState}>Type at least 2 characters to search.</div>
              ) : items.length === 0 ? (
                <div className={styles.emptyState}>
                  {resultsQ.isPending ? 'Searching…' : 'No tickets match. Try widening the filters.'}
                </div>
              ) : items.map((t) => {
                const proj = projectsById.get(t.projectId);
                const author = membersById.get(t.createdBy);
                return (
                  <button key={t.id} type="button" className={styles.resultItem} onClick={() => onResultClick(t)}>
                    <div className={styles.resultMain}>
                      <div className={styles.resultTitle}>{t.title || 'Untitled capture'}</div>
                      {t.descriptionPreview && (
                        <div className={styles.resultPreview}>{t.descriptionPreview}</div>
                      )}
                      <div className={styles.resultMeta}>
                        {proj && <Badge>{proj.name}</Badge>}
                        {t.archivedAt && <Badge>archived</Badge>}
                        {t.resolved && <Badge>resolved</Badge>}
                        {t.hasAiActivity && <Badge>AI activity</Badge>}
                        <span className={styles.muted}>
                          {author ? `${author.name} · ` : ''}<When at={t.updatedAt ?? t.createdAt} exact />
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

/**
 * Multi-select combo box for picking workspace members. Renders a
 * trigger button showing the selection count; click opens a popover
 * with a search filter + checkable list. Used for both Author and
 * Assignees so they share the same UX.
 */
function MemberMultiSelect({
  members, value, onChange, placeholder, ariaLabel,
}: {
  members: User[];
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  placeholder: string;
  ariaLabel: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [filter, setFilter] = useState('');

  const filtered = filter
    ? members.filter((u) => u.name.toLowerCase().includes(filter.toLowerCase()))
    : members;

  const display = value.size === 0
    ? placeholder
    : value.size <= 2
      ? Array.from(value)
          .map((id) => members.find((u) => u.id === id)?.name)
          .filter(Boolean)
          .join(', ')
      : `${value.size} selected`;

  const toggle = (id: string) => {
    const n = new Set(value);
    if (n.has(id)) n.delete(id); else n.add(id);
    onChange(n);
  };

  const open = () => {
    if (!triggerRef.current) return;
    setAnchor(triggerRef.current.getBoundingClientRect());
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.combo}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={!!anchor}
        onClick={open}
      >
        <span className={value.size === 0 ? styles.muted : undefined}>{display}</span>
        <ChevronDown size={14} className={styles.muted} />
      </button>
      <Popover
        open={!!anchor}
        onClose={() => { setAnchor(null); setFilter(''); }}
        anchor={anchor}
        width={300}
      >
        <div className={styles.comboBody}>
          <input
            autoFocus
            className={styles.comboSearch}
            placeholder="Filter members…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className={styles.comboList} role="listbox">
            {filtered.length === 0 ? (
              <div className={styles.muted}>No matches.</div>
            ) : filtered.map((u) => (
              <label key={u.id} className={styles.comboItem}>
                <Checkbox checked={value.has(u.id)} onChange={() => toggle(u.id)} />
                <Avatar name={u.name} size="sm" />
                <span>{u.name}</span>
              </label>
            ))}
          </div>
          {value.size > 0 && (
            <div className={styles.comboFooter}>
              <button type="button" className={styles.comboClear} onClick={() => onChange(new Set())}>
                Clear ({value.size})
              </button>
            </div>
          )}
        </div>
      </Popover>
    </>
  );
}

/** A filter's wall-clock time as the moment it is in the person's zone, or nothing. */
/** A filter's bound as a moment; an end takes in the whole second it names. */
function instantOf(value: string, end = false): string | undefined {
  return value ? (end ? wallTimeEndIn(value) : wallTimeIn(value))?.toISOString() : undefined;
}
